import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { Label } from "~/atoms/label";
import { CenterSpinner } from "~/atoms/spinner";
import { createDocumentShares } from "~/api/v2/requests";
import { useShareTargets } from "~/api/v2/useDocumentShares";

interface Props {
  documentId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onShared: () => void;
}

export const ShareDocumentDialog = ({
  documentId,
  open,
  onOpenChange,
  onShared,
}: Props) => {
  const { t } = useTranslation();
  const {
    data: targets,
    isLoading,
    mutate,
  } = useShareTargets(open ? documentId : undefined);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggle = (id: number, checked: boolean) =>
    setSelectedIds((ids) =>
      checked ? [...ids, id] : ids.filter((selected) => selected !== id),
    );

  const handleOpenChange = (next: boolean) => {
    if (!next) setSelectedIds([]);
    onOpenChange(next);
  };

  const handleShare = async () => {
    setIsSubmitting(true);
    try {
      await createDocumentShares(documentId, selectedIds);
      toast.success(t(($) => $.documentSharing.sharedSuccess));
      void mutate();
      onShared();
      handleOpenChange(false);
    } catch {
      toast.error(t(($) => $.documentSharing.sharedError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(($) => $.documentSharing.dialog.title)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.documentSharing.dialog.description)}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <CenterSpinner />
        ) : targets && targets.length > 0 ? (
          <fieldset className="flex flex-col gap-3">
            <legend className="text-sm font-medium">
              {t(($) => $.documentSharing.dialog.targetsLabel)}
            </legend>
            {targets.map((target) => {
              const inputId = `share-target-${target.id}`;
              return (
                <div key={target.id} className="flex items-center gap-2">
                  <Checkbox
                    id={inputId}
                    disabled={target.alreadyShared}
                    checked={
                      target.alreadyShared || selectedIds.includes(target.id)
                    }
                    onCheckedChange={(checked) =>
                      toggle(target.id, checked === true)
                    }
                  />
                  <Label htmlFor={inputId}>{target.name}</Label>
                  {target.alreadyShared && (
                    <span className="text-muted-foreground text-xs">
                      {t(($) => $.documentSharing.dialog.alreadyShared)}
                    </span>
                  )}
                </div>
              );
            })}
          </fieldset>
        ) : (
          <p className="text-muted-foreground text-sm">
            {t(($) => $.documentSharing.dialog.noTargets)}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t(($) => $.commons.buttons.cancel)}
          </Button>
          <Button
            onClick={handleShare}
            disabled={selectedIds.length === 0 || isSubmitting}
          >
            {t(($) => $.documentSharing.dialog.submit)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
